import React, { useState, useEffect, useMemo } from 'react'
import './nav.css'
import useAuth from '../../hooks/useAuth';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faGlobe } from '@fortawesome/free-solid-svg-icons';
import { useTranslation } from 'react-i18next';
import axios from '../../api/axios';
import useIsMobile from '../../hooks/useIsMobile';
import NavItems from './NavItems';
import NavItemsMobile from './NavItemsMobile';
import { Link } from 'react-router-dom';
import { defaultLanguage } from "../../constants";

const LANG_URL = '/contact';
const WEBSITE_URL = '/websiteAuth/calendar';
const DASH_URL = '/dashboard';
const PUBLIC_PROPERTIES_URL = '/properties/public';

export default function Nav() {

  //hooks
  const art = defaultLanguage;
  const title = "accomodationForm";
  const { i18n } = useTranslation();
  const { auth, username } = useAuth();
  const [languages, setLanguages] = useState([]);
  const isMobile = useIsMobile();
  const [calendar, setCalendar] = useState([]);
  const [showPrices, setShowPrices] = useState(true);
  const [showBlogs, setShowBlogs] = useState(true);
  const [hasSubProperties, setHasSubProperties] = useState(false);

  const tenantKey = useMemo(() => {
    return String(username || "").trim().toLowerCase();
  }, [username]);

  const isSubTenant = useMemo(() => {
    const host = String(window?.location?.hostname || "").trim().toLowerCase();
    const effective = (host && host !== "localhost" && host !== "127.0.0.1")
      ? host
      : String(username || "").trim().toLowerCase();

    const parts = effective.split(".").filter(Boolean);
    return parts.length > 2;
  }, [username]);

  const showBookNow = useMemo(() => {
    const hasCalendar = calendar && calendar !== "no";
    if (!hasCalendar) return false;
    if (isSubTenant) return true;
    return !hasSubProperties;
  }, [calendar, hasSubProperties, isSubTenant]);

  useEffect(() => {
    const getContent = async () => {
      await axios.get(`${WEBSITE_URL}`, {
        params: { username: tenantKey },
      }).then((res) => {
        setCalendar(res?.data?.calendar);
      }).catch((err) => {
        console.log(err);
      });

      if (!isSubTenant) {
        const host = String(window?.location?.hostname || "").trim().toLowerCase();
        const mainDomain = (host && host !== "localhost" && host !== "127.0.0.1")
          ? host
          : tenantKey;
        axios.get(`${PUBLIC_PROPERTIES_URL}`, { params: { mainDomain } })
          .then((res) => {
            const list = Array.isArray(res?.data?.properties) ? res.data.properties : [];
            setHasSubProperties(list.length > 0);
          })

          .catch(() => {
            setHasSubProperties(false);
          });
      } else {
        setHasSubProperties(false);
      }

      axios.get(`${LANG_URL}`, {
        params: { username: tenantKey, art: "nl", title: "contact" },
      }).then((res) => {
        setLanguages(res?.data?.entry?.languages?.split(","));
      }).catch((err) => {
        console.log(err);
      });
      axios.get(`${DASH_URL}`, {
        params: { username: tenantKey, art, title },
      }).then((res) => {
        setShowPrices(res?.data?.entry?.showPrices);
        setShowBlogs(res?.data?.entry?.showBlogs);
      }).catch((err) => {
        console.log(err);
      });
    };
    getContent();
  }, [tenantKey, art, isSubTenant]);

  useEffect(() => {
    if (!isMobile) return;

    const addGoogleTranslateScript = () => {
      if (window.google && window.google.translate) return;
      if (document.getElementById('google-translate-script')) return;

      const script = document.createElement('script');
      script.id = 'google-translate-script';
      script.src = 'https://translate.google.com/translate_a/element.js?cb=googleTranslateElementInit';
      script.async = true;
      document.body.appendChild(script);
    };

    window.googleTranslateElementInit = () => {
      if (!document.getElementById('google_translate_element')) return;
      // Google's own widget UI (dropdown, logo, "Powered by Google
      // Translate" attribution) renders inconsistently across devices/OSes
      // and can't be reliably forced into a compact form via CSS alone.
      // The element stays hidden (see .translate-widget-hidden in nav.css)
      // and is driven programmatically instead, via the small custom "G"
      // icon rendered in the nav below.
      new window.google.translate.TranslateElement(
        {
          pageLanguage: 'en',
          autoDisplay: false,
        },
        'google_translate_element'
      );
    };
    // A previous auto-translate can leave a `googtrans` cookie behind, which
    // makes Google re-translate on every future load regardless of
    // autoDisplay. Clear it so the page starts untranslated.
    document.cookie = 'googtrans=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/';
    document.cookie = `googtrans=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=${window.location.hostname}`;
    addGoogleTranslateScript();
    // The top banner Google shows while a translation is active pushes the
    // page down via an inline `top` offset on body/html. iOS Safari
    // re-applies that offset on resize/orientation-change more aggressively
    // than Android Chrome, so actively strip it to keep the icon compact.
    const resetTranslateOffset = () => {
      if (document.body.style.top && document.body.style.top !== '0px') {
        document.body.style.top = '0px';
      }
      if (document.documentElement.style.top && document.documentElement.style.top !== '0px') {
        document.documentElement.style.top = '0px';
      }
    };

    const bodyObserver = new MutationObserver(resetTranslateOffset);
    bodyObserver.observe(document.body, { attributes: true, attributeFilter: ['style'] });
    const htmlObserver = new MutationObserver(resetTranslateOffset);
    htmlObserver.observe(document.documentElement, { attributes: true, attributeFilter: ['style'] });

    // Keep the callback and script loaded once injected, even if the component unmounts
    // during React Strict Mode or hydration. The translate script may still call the callback.
    return () => {
      bodyObserver.disconnect();
      htmlObserver.disconnect();
    };
  }, [isMobile]);

  const translateWithGoogle = (code) => {
    const select = document.querySelector('#google_translate_element select.goog-te-combo');
    if (!select) return;
    select.value = code;
    select.dispatchEvent(new Event('change'));
  };

  return (
    <div>
      <ul>
        <div className="navbar">
          {isMobile ? (
            <>
              <NavItemsMobile
                isAdmin={auth.role}
                calendarUrl={calendar}
                showBookNow={showBookNow}
                showPrices={showPrices}
                showBlogs={showBlogs}
              />
              <div className="navbar-right-mobile">
                {showBookNow && (
                  <Link className="book-now-btn-mobile-nav" to={calendar}>
                    Book Now
                  </Link>
                )}
                <div id="google_translate_element" className="translate-widget-hidden" />
                <li className="dropdown">
                  <div className="nav-link">
                    <svg
                      width="15"
                      height="15"
                      viewBox="0 0 48 48"
                      className="google-icon-mobile"
                      style={{ display: 'block' }}
                      aria-label="Translate"
                    >
                      <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.9 29.3 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34.6 6.5 29.6 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.7-.4-3.5z" />
                      <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.5 16 18.9 13 24 13c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34.6 6.5 29.6 4 24 4 16.3 4 9.7 8.3 6.3 14.7z" />
                      <path fill="#4CAF50" d="M24 44c5.2 0 10-2 13.6-5.2l-6.3-5.3C29.4 35.5 26.8 36 24 36c-5.2 0-9.6-3.1-11.3-7.5l-6.5 5C9.5 39.6 16.2 44 24 44z" />
                      <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.3-2.3 4.3-4.2 5.7l6.3 5.3C39.6 37.5 44 31.5 44 24c0-1.3-.1-2.7-.4-3.5z" />
                    </svg>
                  </div>
                  {languages !== null && (
                    <div className="dropdown-content">
                      {languages?.map((code) => (
                        <button
                          key={code}
                          value={code}
                          className="drop"
                          onClick={() => translateWithGoogle(code)}
                        >
                          {code}
                        </button>
                      ))}
                    </div>
                  )}
                </li>
                <li className="dropdown">
                  <div className="nav-link">
                    <FontAwesomeIcon
                      icon={faGlobe}
                      className="globe-icon-mobile"
                    />
                  </div>
                  {languages !== null && (
                    <div className="dropdown-content">
                      {languages?.map((code) => (
                        <button
                          key={code}
                          value={code}
                          className="drop"
                          onClick={(e) => {
                            i18n.changeLanguage(e.target.value);
                          }}
                        >
                          {code}
                        </button>
                      ))}
                    </div>
                  )}
                </li>
              </div>
            </>
          ) : (
            <>
              <NavItems isAdmin={auth.role} calendarUrl={calendar} showPrices={showPrices} showBlogs={showBlogs} />
              <li className="dropdown">
                <div className="nav-link">
                  <div style={{ display: "inline-flex" }}>
                    {i18n.language}
                    <span className="hspace"></span>
                  </div>
                  <FontAwesomeIcon
                    icon={faGlobe}
                    className="absolute top-1/2 right-0 transform -translate-y-1/2 pointer-events-none text-black"
                  />
                </div>
                {languages !== null && (
                  <div className="dropdown-content">
                    {languages?.map((code) => (
                      <button
                        key={code}
                        value={code}
                        className="drop"
                        onClick={(e) => {
                          i18n.changeLanguage(e.target.value);
                        }}
                      >
                        {code}
                      </button>
                    ))}
                  </div>
                )}
              </li>
              {showBookNow && (
                <Link className="book-now-btn book-now-btn-margin" to={calendar}>
                  Book Now
                </Link>
              )}
            </>
          )}
        </div>
      </ul>
    </div>
  );
}